/**
 * "How it works" — an in-app, read-only render of the SOC 1 review process
 * (docs/SOC1_process_flow_V4.md). Each section shows the human-decision
 * flowchart (pre-rendered SVG in /public/process-flow), then *Behind the
 * scenes* — the AI plugins + the deterministic rules. The change log is
 * intentionally omitted.
 *
 * Diagrams are static SVGs (no runtime diagramming dependency — see the
 * security review in the plan). Regenerate them from the doc's ```mermaid```
 * blocks with @mermaid-js/mermaid-cli when the flow changes:
 *   mmdc -i <block>.mmd -o public/process-flow/<name>.svg -b transparent \
 *        -c '{"htmlLabels":false,"flowchart":{"htmlLabels":false}}'
 * (htmlLabels:false keeps labels as <text> so they render inside <img>.)
 */
import {
  Box,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  Table,
} from "@cloudscape-design/components";

type Row = { id: string; cells: React.ReactNode[] };

function InfoTable({ headers, rows }: { headers: string[]; rows: Row[] }) {
  return (
    <Table
      variant="embedded"
      contentDensity="compact"
      wrapLines
      trackBy="id"
      items={rows}
      columnDefinitions={headers.map((h, i) => ({
        id: `c${i}`,
        header: h,
        cell: (r: Row) => r.cells[i] ?? "",
      }))}
    />
  );
}

/** A flowchart on a light "canvas" so it stays legible in light or dark mode. */
function Diagram({ name, alt }: { name: string; alt: string }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e9ebed",
        borderRadius: 8,
        padding: 16,
        overflowX: "auto",
      }}
    >
      <img
        src={`/process-flow/${name}.svg`}
        alt={alt}
        style={{ display: "block", maxWidth: "100%", height: "auto", margin: "0 auto" }}
      />
    </div>
  );
}

const code: React.CSSProperties = {
  fontFamily: "Monaco, Consolas, monospace",
  fontSize: "0.85em",
  fontWeight: 700,
};
const C = ({ children }: { children: React.ReactNode }) => <span style={code}>{children}</span>;

const PLUGIN_HEADERS = ["AI plugin", "What it drafts", "Feeds"];

export function HowItWorks() {
  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            description="You review each SOC 1 by walking five decisions (scope → bridge letter → control exceptions → CUECs → documentation); AI plugins draft the facts and the system auto-clears anything it's sure of, so you only act on what's left in the action-items inbox."
          >
            How it works
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="strong">
            Start here: upload your first SOC 1 in the Reports tab — review work then appears as action
            items in your inbox.
          </Box>
          <Box color="text-body-secondary" fontSize="body-s">
            <C>C#</C> = a check you decide · <C>A#</C> = an action item in your inbox · <C>AIP-N</C>{" "}
            = an AI plugin that drafts in the background.
          </Box>
          <Box color="text-body-secondary" fontSize="body-s">
            Each section leads with the flowchart of what <strong>you</strong> do. Under it,{" "}
            <em>Behind the scenes</em> shows the AI plugins that extract and draft, and the
            deterministic rules that auto-clear high-confidence items. The flowcharts contain only
            human decisions — no AI.
          </Box>
          <Box variant="h3">Overview</Box>
          <Diagram name="overview" alt="Overview: PDF → scope → bridge letter → controls → CUECs → documentation → workpaper" />
          <Box color="text-body-secondary" fontSize="body-s">
            The bridge letter is triggered when the report period only <strong>partially</strong>{" "}
            covers your fiscal year, but the app surfaces it <strong>after CUECs</strong> — bridge
            letters arrive months after year-end, so scope/controls/CUECs are reviewed immediately
            and the bridge work waits.
          </Box>
        </SpaceBetween>
      </Container>

      <ExpandableSection variant="container" headerText="1. Scope review">
        <SpaceBetween size="m">
          <Box>
            What you decide: is this the right report, for the right period, of the right type, from
            a credible auditor, with a clean opinion, and are any subservice organizations relevant
            to you?
          </Box>
          <Diagram name="scope" alt="Scope review decision tree" />
          <Box fontSize="body-s">
            <strong>Action items you may see:</strong> <C>A1</C> Get new SOC 1 · <C>A2</C> Confirm
            the period · <C>A4</C> Review service auditor · <C>A5</C> Document the modified-opinion
            impact / non-reliance · <C>A8</C> one per subservice organization (request its SOC 1, or
            document N/A).
          </Box>
          <Box variant="h4">Behind the scenes</Box>
          <InfoTable
            headers={PLUGIN_HEADERS}
            rows={[
              {
                id: "aip1",
                cells: [
                  "AIP-1 Scope extractor",
                  "Runs once; extracts the in-scope service (C1), period (C2), SOC type (C3), auditor (C4), opinion (C5), and subservice method + carve-out list (C6) — each with a confidence",
                  "C1–C6",
                ],
              },
              {
                id: "aip2",
                cells: [
                  "AIP-2 Subservice summarizer",
                  "Runs per subservice org; summarizes what it does and whether it's carved-out vs inclusive, against your Company Profile. It does not judge applicability — that's your call",
                  "C6",
                ],
              },
            ]}
          />
          <Box color="text-body-secondary" fontSize="body-s">
            <strong>Auto-cleared for you:</strong> C2 is decided from the dates alone (covers FY →
            Full; short by &gt;1 month → Partial). C3/C4/C5 auto-clear only when AIP-1 is
            high-confidence on a clean pass (Type II / reputable auditor / unmodified opinion).{" "}
            <strong>C1 always opens</strong> — the AI can't see which services you actually consume.
            Subservice orgs are never auto-decided; an inclusive one needs no separate SOC 1.
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      <ExpandableSection variant="container" headerText="1.5 Bridge letter">
        <SpaceBetween size="m">
          <Box>
            Only when the period is <strong>Partial</strong>. The SOC 1 typically ends 2–3 months
            before your fiscal year-end; the vendor issues a bridge letter after year-end to cover
            the gap. The section is revealed once you accept C2 = Partial and stays "obtain the
            bridge letter" until one is uploaded.
          </Box>
          <Diagram name="bridge" alt="Bridge letter decision tree" />
          <Box fontSize="body-s">
            <strong>Action items you may see:</strong> <C>A3</C> Obtain the bridge letter · <C>A2</C>{" "}
            Document the gap (opens if the letter is unobtainable, doesn't reach year-end, or the
            changes aren't acceptable) · <C>A3.1</C> Evaluate material changes.
          </Box>
          <Box variant="h4">Behind the scenes</Box>
          <InfoTable
            headers={PLUGIN_HEADERS}
            rows={[
              {
                id: "aip7",
                cells: [
                  "AIP-7 Bridge letter processor",
                  "Runs once a letter is uploaded; extracts the letter date and management's assertion (material changes: yes / no)",
                  "C2.1, C2.2",
                ],
              },
            ]}
          />
          <Box color="text-body-secondary" fontSize="body-s">
            <strong>Auto-cleared for you:</strong> a clean letter (covers year-end, no material
            changes) clears the bridge with no write-up needed. AIP-7 supplies dates and the
            assertion; the A2 risk-assessment narrative is always <strong>yours</strong> to write.
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      <ExpandableSection variant="container" headerText="2. Control exceptions">
        <SpaceBetween size="m">
          <Box>
            For each exception the auditor noted in Section IV, decide whether a compensating
            control — at the service organization or one of your own — covers it, or whether it's an
            unmitigated gap you must document.
          </Box>
          <Diagram name="controls" alt="Control exceptions decision tree" />
          <Box fontSize="body-s">
            <strong>Action items you may see:</strong> <C>A13</C> one per exception until you pick a
            resolving Conclusion and Mark done. Unmitigated and not-yet-concluded stay open; you
            must document a rationale before marking any exception done.
          </Box>
          <Box variant="h4">Behind the scenes</Box>
          <InfoTable
            headers={PLUGIN_HEADERS}
            rows={[
              {
                id: "aip3",
                cells: [
                  "AIP-3 Controls extractor",
                  "Runs once; finds every Section IV exception with its control objective, the auditor's note, the service org's response, and the PDF page; flags whether the objective is qualified",
                  "the exception list",
                ],
              },
              {
                id: "aip4",
                cells: [
                  "AIP-4 Exception assessor",
                  "Runs per exception; reads for service-org / our-org / no compensating controls — shown as read-only context that pre-selects a suggested Conclusion",
                  "your Conclusion",
                ],
              },
            ]}
          />
          <Box color="text-body-secondary" fontSize="body-s">
            <strong>Auto-cleared for you:</strong> when AIP-4 is high-confidence that an exception is
            clearly mitigated, the card is pre-marked done — you can still reopen and override it.
            Nothing else auto-resolves; you own the Conclusion and the narrative.
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      <ExpandableSection variant="container" headerText="3. CUEC review">
        <SpaceBetween size="m">
          <Box>
            For each complementary user-entity control the SOC 1 puts on you, decide: does it apply,
            and do your own controls cover it?
          </Box>
          <Diagram name="cuecs" alt="CUEC review decision tree" />
          <Box fontSize="body-s">
            <strong>Action items you may see:</strong> <C>A19</C> for any CUEC with partial / no
            coverage (and any not-applicable one the AI wasn't sure about) until you document a
            Management Response and Mark done.
          </Box>
          <Box variant="h4">Behind the scenes</Box>
          <InfoTable
            headers={PLUGIN_HEADERS}
            rows={[
              {
                id: "aip5",
                cells: [
                  "AIP-5 CUEC extractor",
                  "Runs once; finds every CUEC and writes a ≤15-word short title so the list is readable",
                  "the CUEC list",
                ],
              },
              {
                id: "aip6",
                cells: [
                  "AIP-6 CUEC mapper",
                  "Runs per CUEC; judges applicability, proposes the RCM control mapping + coverage (full/partial/none/NA), and drafts the Management Response",
                  "coverage + your response",
                ],
              },
            ]}
          />
          <Box color="text-body-secondary" fontSize="body-s">
            <strong>Auto-cleared for you:</strong> a high-confidence <strong>Full</strong> coverage
            (with a mapped control) or a high-confidence <strong>N/A</strong> auto-completes.{" "}
            <strong>Partial and None never auto-complete</strong> — they always stay open with an
            alert, because a coverage gap needs your validation.
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      <ExpandableSection variant="container" headerText="4. Documentation and sign-off">
        <SpaceBetween size="m">
          <Diagram name="documentation" alt="Documentation and sign-off flow" />
          <Box color="text-body-secondary" fontSize="body-s">
            Sign-off happens outside the app — you circulate the DOCX, capture signatures, and
            retain the final copy on your own systems.
          </Box>
          <Box variant="h4">Behind the scenes</Box>
          <Box color="text-body-secondary" fontSize="body-s">
            None. The workpaper is assembled from your accepted decisions by a fixed template —{" "}
            <strong>no generative AI</strong> — so every paragraph traces back to a specific
            decision. Your corrections win over the AI's drafts in the export.
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      <Container header={<Header variant="h2">Deterministic code configurations</Header>}>
        <SpaceBetween size="m">
          <Box color="text-body-secondary" fontSize="body-s">
            The hardcoded rules the system applies on its own, so you can see exactly what's
            automated and what isn't.
          </Box>
          <InfoTable
            headers={["Rule", "Detail"]}
            rows={[
              {
                id: "r1",
                cells: [
                  <strong>C1 always opens</strong>,
                  "The in-scope-service check is forced to low confidence and never auto-accepts — the AI can't see which services you actually consume. You confirm it on every report.",
                ],
              },
              {
                id: "r2",
                cells: [
                  <strong>Auto-accept needs "high" confidence + a clean pass</strong>,
                  "A check auto-clears only when AIP-1 reports high and the answer is a clean pass. Any other confidence, or a non-clean answer, opens an action item.",
                ],
              },
              {
                id: "r3",
                cells: [
                  <strong>C2 is decided from dates, not AI confidence</strong>,
                  "Period end ≥ your fiscal year-end → Full; short by > 1 month → Partial (triggers the bridge letter); dates missing → opens for you. Your fiscal year-end is a required input before upload.",
                ],
              },
              {
                id: "r4",
                cells: [
                  <strong>Clean-pass values that auto-clear</strong>,
                  "C3 = SOC 1 Type II · C4 = reputable auditor · C5 = unmodified opinion · C6 = no subservice organizations.",
                ],
              },
              {
                id: "r5",
                cells: [
                  <strong>Reputable-auditor list</strong>,
                  "Big-4 + national firms and dedicated SOC firms (A-LIGN, Schellman, Coalfire, and peers) → \"acceptable\" at high confidence. A genuinely unfamiliar firm → \"unknown\" → opens A4.",
                ],
              },
              {
                id: "r6",
                cells: [
                  <strong>Subservice orgs are never auto-decided</strong>,
                  "Each carved-out org is your per-org decision (A8: request its SOC 1 / link an existing one / document N/A). An inclusive org is covered by this report and needs no separate SOC 1. AIP-2 summarizes only.",
                ],
              },
              {
                id: "r7",
                cells: [
                  <strong>Every exception needs a Conclusion</strong>,
                  "A13 opens for each Section IV exception. Only Mitigated by service org / Mitigated by our controls / N/A resolve; Unmitigated and unconcluded stay open. A high-confidence \"clearly mitigated\" read may pre-mark done, but you can override. A documented rationale comment is required to mark done.",
                ],
              },
              {
                id: "r8",
                cells: [
                  <strong>CUEC coverage gates auto-complete</strong>,
                  "High-confidence Full (with a mapped control) or N/A auto-completes. Partial / None — and any low-confidence N/A — never auto-complete; they stay open and require a documented Management Response to mark done.",
                ],
              },
              {
                id: "r9",
                cells: [
                  <strong>The workpaper is deterministic</strong>,
                  "Assembled from a fixed template with no generative AI; your overrides win over AI values. \"Mark report complete\" requires at least one workpaper to have been generated.",
                ],
              },
              {
                id: "r10",
                cells: [
                  <strong>AI never blocks you</strong>,
                  "Every plugin run is logged to the Activity Log (model, duration, prompt version, outcome). If a plugin fails, it's logged and the normal review action item carries the follow-up; the section is never blocked.",
                ],
              },
            ]}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Chat plugin</Header>}>
        <SpaceBetween size="s">
          <Box color="text-body-secondary" fontSize="body-s">
            A cross-cutting helper in a side panel, available at every step. The thread is stored
            per user and reloads on refresh.
          </Box>
          <Box fontSize="body-s">
            <strong>Available now (read-only):</strong> list reports · open a report · search the
            RCM · search the SOC 1 PDF · list open findings.
          </Box>
          <Box color="text-body-secondary" fontSize="body-s">
            <strong>Coming later:</strong> accept a check, edit/close an action item, upload
            evidence, memory, ask-a-question, propose-a-plan, list/read attachments.
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
